import { DataAccess, HttpGetRequestOptions, ResponseMessage } from "../../dataaccess/data.access";
import { OrganizationService } from "../../services/organization.service";
import { EventType, EventTypeService } from "../../services/master.definitions/event.misc/event.type.service";
import { S25Util } from "../../util/s25-util";
import { AttachmentItem } from "../../services/attachment.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { Cache, Invalidate } from "../../decorators/cache.decorator";
import { Workflow } from "../workflow/service/Workflow";
import { Proto } from "../../pojo/Proto";
import { Task } from "../../pojo/Task";

export enum IngestionPointCategory {
    ENGAGEMENT = "ENGAGEMENT",
    EVENT_SURVEY = "EVENT_SURVEY",
}

export const INGESTION_CATEGORIES: IngestionPointCategory[] = [
    IngestionPointCategory.ENGAGEMENT,
    IngestionPointCategory.EVENT_SURVEY,
];

export const INGESTION_FIELD_TYPE_ID = 8001;
export const INGESTION_POINT_TYPE_ID = 11_001;

export interface IngestionPoint {
    ingestionPointId?: number;
    ingestionPointName?: string;
    category?: IngestionPointCategory;
    subCat?: string;
    description?: string;
    enabled?: boolean;
    instance?: string;
    createDate?: Proto.ISODateString;
    lastModDate?: Proto.ISODateString;
    lastModUser?: string;
    fieldDefinitions?: IngestionFieldDefinition[];
    workflowId?: number;
    defaultWorkflowId?: number;
    activeStartDate?: Proto.ISODateString;
    activeEndDate?: Proto.ISODateString;
    deleted?: boolean;
    deletedDate?: Proto.ISODateString;
    deletedUser?: string;

    // Frontend convenience fields
    subCategoryName?: string;
}

export interface IngestionFieldDefinition {
    fieldDefinitionId?: number;
    ingestionPointId?: number;
    instance?: string;
    fieldLabel?: string;
    fieldType?:
        | "SELECT"
        | "MULTISELECT"
        | "TEXT"
        | "SECTION"
        | "FILE"
        | "EVENT_TYPE"
        | "ORGANIZATION"
        | "OBJECT_OCCURRENCE"
        | "FORM"
        | "REGISTRANTS";
    answerType?: AnswerType;
    infoText?: string;
    options?: IngestionFieldOption[];
    sortOrder?: number;
    hidden?: boolean;
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    regexValid?: string;
    reportingFieldCode?: string;
    defaultValue?: string;

    // Frontend convenience fields
    newAnswer?: {
        text?: string;
    };
    answers?: IngestionFieldOption[];
    singleAnswer?: IngestionFieldOption;
    specialFieldItem?: DropDownItem; // For EVENT_TYPE and other special field types
    jsonFieldItem?: ObjectOccurrenceData | RegistrantsData;
    longAnswer?: string;
    answerId?: number;
    validationError?: string;
    showInfoText?: boolean;
    files?: AttachmentItem[]; // For FILE field types
    fieldCode?: string;
    prevFieldLabel?: string; // For tracking previous field label for auto-generation logic
}

export interface IngestionFieldOption {
    fieldDefinitionId?: number;
    option?: string;
    ingestionPointId?: number;
    instance?: string;
    sortOrder?: number;
}

export interface IngestionFieldAnswer {
    answerId?: number;
    instance?: string;
    fieldDefinitionId?: number;
    contId?: number;
    ingestionPointId?: number;
    submissionId?: number;
    answerString?: string;
    answerLargeString?: string;
    answerInteger?: number;
    answerFloat?: number;
    answerBoolean?: boolean;
    answerDatetime?: string;
    answerJson?: string;
    answerRaw?: string;
    fieldDefinition?: IngestionFieldDefinition;
}

export interface IngestionSubmission {
    submissionId?: number;
    instance?: string;
    contId?: number;
    ingestionPointId?: number;
    createDate?: Proto.ISODateString;
    lastModDate?: Proto.ISODateString;
    submitted?: boolean;
    answers?: IngestionFieldAnswer[];
    submissionDate?: Proto.ISODateString;
    workflowProcessId?: number;
    currentWorkflowStageId?: number;
    currentWorkflowStage?: Workflow.WorkflowStage; // only stageName is populated
    ingestionPoint?: IngestionPoint; // only ingestionPointName is populated
    todoId?: number; // only for approval / denial on todos
    workflowStatus?: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "DENIED" | "REQUESTED_CHANGES";
    lastModUser?: string;
    // Frontend-only properties
    submitterName?: string; // Contact name for display
    showingRequestedChanges?: boolean; // UI state for showing/hiding requested changes
    requestedChangesList?: Task.DBTodo[]; // List of requested changes from todo comments
}

export enum AnswerType {
    STRING = "STRING",
    LARGE_STRING = "LARGE_STRING",
    INTEGER = "INTEGER",
    FLOAT = "FLOAT",
    BOOLEAN = "BOOLEAN",
    DATETIME = "DATETIME",
    FILE = "FILE",
    JSON = "JSON",
}

// Object Occurrence data structures
export interface ObjectOccurrence {
    occurrenceUuid: string; // unique ID for this occurrence
    startDate: Proto.ISODateString; // ISO datetime string
    endDate: Proto.ISODateString; // ISO datetime string
    locationIds: number[]; // array of location item IDs
}

export interface ObjectOccurrenceData {
    occurrences: ObjectOccurrence[];
}

export interface RegistrantsData {
    type: string;
    items: { itemId: number; itemName?: string }[];
}

export interface ReportingFieldInfo {
    reportingFieldCode: string;
    answerType: AnswerType;
    fieldType?: "SELECT" | "MULTISELECT" | "TEXT" | "SECTION" | "FILE" | "EVENT_TYPE" | "ORGANIZATION" | "REGISTRANTS";
    dateModifier?: DateModifier; // Frontend convenience property for group by operations
}

export interface SearchRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
    projectionFields?: string[];
    predicate?: any;
    pageNum?: number;
    pageSize?: number;
    sorts?: string[];
    pageKey?: string;
}

export enum DateModifier {
    YEAR = "YEAR",
    MONTH_YEAR = "MONTH_YEAR",
    DAY_MONTH_YEAR = "DAY_MONTH_YEAR",
    DAY_OF_WEEK = "DAY_OF_WEEK",
}

export interface ReportRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
    groupByFields?: ReportingFieldInfo[];
    aggregations?: AggregationSpec[];
    predicate?: any;
    includeForm?: boolean;
}

export interface AggregationSpec {
    type: "COUNT" | "COUNT_DISTINCT" | "SUM" | "AVG";
    field: string;
}

export interface BaseResponse<T = unknown> {
    data: T[];
}

export interface SearchResponse extends BaseResponse<IngestionSubmission> {
    total: number;
    totalPages: number;
    pageNum: number;
    pageSize: number;
    pageCacheKey?: string;
    fastFirstPage: boolean;
    error?: string;
    errorCode?: string;
}

export interface ReportResponse extends BaseResponse<{ [key: string]: string | number }> {
    err_msg?: string;
    err_code?: string;
}

export interface SaveSearchRequest {
    name: string;
    searchRequest: SearchRequest;
}

export interface SaveReportRequest {
    name: string;
    reportRequest: ReportRequest;
}

export interface SavedQuery {
    id: number;
    name: string;
    type: string;
    description: string;
    createdBy: string;
    createdDate: string;
}

export interface DistinctFieldsRequest {
    ingestionPointIds?: number[];
    searchAllForms: boolean;
}

export class DataIngestionService {
    // Private helper method to initialize frontend convenience fields for field definitions
    private static initializeFieldDefinition(fieldDef: IngestionFieldDefinition): void {
        // Initialize prevFieldLabel for auto-generation logic
        fieldDef.prevFieldLabel = fieldDef.fieldLabel;
    }

    // Private helper method to initialize field definitions for ingestion points
    private static initializeFieldDefinitions(ingestionPoint: IngestionPoint): void {
        if (ingestionPoint.fieldDefinitions) {
            ingestionPoint.fieldDefinitions.forEach((fieldDef) => this.initializeFieldDefinition(fieldDef));
        }
    }

    // Ingestion Point Management
    public static async getIngestionPoints(
        category?: IngestionPointCategory,
        subCategory?: string,
        includeDeleted?: boolean,
    ): Promise<IngestionPoint[]> {
        let url = "/ingestion/points.json";
        const params = new URLSearchParams();

        if (category) {
            params.append("category", category);
        }
        if (subCategory) {
            params.append("subCategory", subCategory);
        }
        if (S25Util.isDefined(includeDeleted)) {
            params.append("includeDeleted", includeDeleted!.toString());
        }

        if (params.toString()) {
            url += "?" + params.toString();
        }

        const ingestionPoints = (await DataAccess.get<IngestionPoint[]>(url)) || [];
        // Initialize field definitions for all ingestion points
        ingestionPoints.forEach((point) => this.initializeFieldDefinitions(point));
        return ingestionPoints;
    }

    public static async getIngestionPoint(
        ingestionPointId: number,
        options?: HttpGetRequestOptions,
    ): Promise<IngestionPoint> {
        const ingestionPoint = await DataAccess.get<IngestionPoint>(
            `/ingestion/point/${ingestionPointId}.json`,
            options,
        );
        // Initialize field definitions
        this.initializeFieldDefinitions(ingestionPoint);
        return ingestionPoint;
    }

    public static async getIngestionPointName(ingestionPointId: number, options?: HttpGetRequestOptions) {
        const form = await DataIngestionService.getIngestionPoint(ingestionPointId, options);
        return form?.ingestionPointName;
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async createIngestionPoint(ingestionPoint: IngestionPoint): Promise<IngestionPoint> {
        // Set default category if not provided
        if (!ingestionPoint.category) {
            ingestionPoint.category = IngestionPointCategory.ENGAGEMENT;
        }
        return await DataAccess.post<IngestionPoint>("/ingestion/point.json", ingestionPoint);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async updateIngestionPoint(
        ingestionPointId: number,
        ingestionPoint: IngestionPoint,
    ): Promise<IngestionPoint> {
        return await DataAccess.put<IngestionPoint>(`/ingestion/point/${ingestionPointId}.json`, ingestionPoint);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async deleteIngestionPoint(ingestionPointId: number): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/point/${ingestionPointId}.json`);
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async undeleteIngestionPoint(ingestionPointId: number): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/point/${ingestionPointId}/undelete.json`);
    }

    public static async populateSubCategoryNames(
        category: IngestionPointCategory,
        ingestionPoints: IngestionPoint[],
    ): Promise<void> {
        if (category === IngestionPointCategory.ENGAGEMENT) {
            await this.populateEngagementSubCategoryNames(ingestionPoints);
        }
        // EVENT_SURVEY doesn't need subcategory names - they're standalone
        // Other categories not supported yet
    }

    private static async populateEngagementSubCategoryNames(ingestionPoints: IngestionPoint[]): Promise<void> {
        if (!ingestionPoints?.length) {
            return;
        }

        // Extract all subCat values and convert to integers
        const subCatIds = ingestionPoints
            .map((point) => point.subCat)
            .filter((subCat) => subCat)
            .map((subCat) => S25Util.parseInt(subCat || ""))
            .filter((id) => id !== 0);

        if (!subCatIds.length) {
            return;
        }

        // Get organization names for all subCat IDs
        const organizations = await OrganizationService.getOrganizationsNameAndFormal(subCatIds);

        // Create a map of itemId -> organization object
        const orgMap = S25Util.fromEntries(organizations.map((org) => [org.itemId!, org]));

        // Set subCategoryName for each ingestion point
        ingestionPoints.forEach((point) => {
            if (point.subCat) {
                const orgId = S25Util.parseInt(point.subCat);
                const org = orgMap[orgId];
                if (org) {
                    point.subCategoryName = org.itemName;
                }
            }
        });
    }

    // Field Definition Management
    @Cache({ immutable: true, targetName: "DataIngestionService" })
    public static async getFieldDefinitions(ingestionPointId: number): Promise<IngestionFieldDefinition[]> {
        const fieldDefinitions =
            (await DataAccess.get<IngestionFieldDefinition[]>(`/ingestion/point/${ingestionPointId}/fields.json`)) ||
            [];
        // Initialize field definitions
        fieldDefinitions.forEach((fieldDef) => this.initializeFieldDefinition(fieldDef));
        return fieldDefinitions;
    }

    @Invalidate({ serviceName: "DataIngestionService" })
    public static async replaceFieldDefinitions(
        ingestionPointId: number,
        fieldDefinitions: IngestionFieldDefinition[],
    ): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/point/${ingestionPointId}/fields.json`, fieldDefinitions);
    }

    // Submission Management
    public static async createSubmission(ingestionPointId: number): Promise<IngestionSubmission> {
        return await DataAccess.post<IngestionSubmission>(`/ingestion/point/${ingestionPointId}/submission.json`, {});
    }

    public static async getSubmission(submissionId: number): Promise<IngestionSubmission> {
        return await DataAccess.get<IngestionSubmission>(`/ingestion/submission/${submissionId}.json`);
    }

    public static async getMySubmissions(): Promise<IngestionSubmission[]> {
        return (await DataAccess.get<IngestionSubmission[]>("/ingestion/my-submissions.json")) || [];
    }

    // Answer Management
    public static async saveAnswersForField(
        submissionId: number,
        fieldDefinitionId: number,
        answers: IngestionFieldAnswer[],
    ): Promise<void> {
        return await DataAccess.post<void>(
            `/ingestion/submission/${submissionId}/field/${fieldDefinitionId}/answers.json`,
            answers,
        );
    }

    public static async saveAnswers(submissionId: number, answers: IngestionFieldAnswer[]): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/submission/${submissionId}/answers.json`, answers);
    }

    public static async getAnswers(submissionId: number): Promise<IngestionFieldAnswer[]> {
        return (
            (await DataAccess.get<IngestionFieldAnswer[]>(`/ingestion/submission/${submissionId}/answers.json`)) || []
        );
    }

    // Submission
    public static async submitSubmission(submissionId: number): Promise<void> {
        return await DataAccess.post<void>(`/ingestion/submission/${submissionId}/submit.json`, {});
    }

    public static async deleteSubmission(submissionId: number): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/submission/${submissionId}.json`);
    }

    /**
     * Get names for event types and organizations
     * This method handles the specific object types used in ingestion forms
     */
    public static async getObjectNames(
        objectType: "EVENT_TYPE" | "ORGANIZATION",
        objectIds: number[],
    ): Promise<{ [key: string]: string }> {
        if (objectIds.length === 0) {
            return {};
        }

        try {
            if (objectType === "EVENT_TYPE") {
                // Use EventTypeService to get event type names
                const eventTypes = await EventTypeService.getSingleton().get();
                const nameMap: { [key: string]: string } = {};
                eventTypes.forEach((eventType: EventType | undefined) => {
                    if (eventType && objectIds.includes(eventType.type_id)) {
                        nameMap[`${eventType.type_id}-EVENT_TYPE`] = eventType.type_name;
                    }
                });
                return nameMap;
            } else if (objectType === "ORGANIZATION") {
                // Use OrganizationService to get organization names
                const organizations = await OrganizationService.getOrganizationsNameAndFormal(objectIds);
                const nameMap: { [key: string]: string } = {};
                organizations.forEach((item) => {
                    if (item.itemName) {
                        nameMap[`${item.itemId}-ORGANIZATION`] = item.itemName;
                    }
                });
                return nameMap;
            }
        } catch (error) {
            console.error(`Error loading ${objectType} names:`, error);
        }

        return {};
    }

    // Search and Reporting
    public static async getDistinctReportingFields(
        ingestionPointIds: number[],
        searchAllForms: boolean,
    ): Promise<ReportingFieldInfo[]> {
        const request: DistinctFieldsRequest = {
            ingestionPointIds,
            searchAllForms,
        };
        return await DataAccess.post<ReportingFieldInfo[]>(`/ingestion/search-report/distinct-fields.json`, {
            DistinctFieldsRequest: request,
        });
    }

    public static async getIngestionPointsForSearchReport(): Promise<IngestionPoint[]> {
        return await DataAccess.get<IngestionPoint[]>(`/ingestion/search-report/ingestion-points.json`);
    }

    public static async executeSearch(request: SearchRequest): Promise<SearchResponse> {
        return await DataAccess.post<SearchResponse>(`/ingestion/search-report/search.json`, {
            SearchRequest: request,
        });
    }

    public static async executeReport(request: ReportRequest): Promise<ReportResponse> {
        return await DataAccess.post<ReportResponse>(`/ingestion/search-report/report.json`, {
            ReportRequest: request,
        });
    }

    public static async saveSearch(name: string, searchRequest: SearchRequest): Promise<SavedQuery> {
        return await DataAccess.post<SavedQuery>(`/ingestion/search-report/save-search.json`, {
            SaveSearchRequest: {
                name,
                searchRequest,
            },
        });
    }

    public static async saveReport(name: string, reportRequest: ReportRequest): Promise<SavedQuery> {
        return await DataAccess.post<SavedQuery>(`/ingestion/search-report/save-report.json`, {
            SaveReportRequest: {
                name,
                reportRequest,
            },
        });
    }

    public static async getSavedSearches(): Promise<SavedQuery[]> {
        return await DataAccess.get<SavedQuery[]>(`/ingestion/search-report/saved-searches.json`);
    }

    public static async getSavedReports(): Promise<SavedQuery[]> {
        return await DataAccess.get<SavedQuery[]>(`/ingestion/search-report/saved-reports.json`);
    }

    public static async getSavedQueries(): Promise<SavedQuery[]> {
        // Combine saved searches and reports
        const [searches, reports] = await Promise.all([this.getSavedSearches(), this.getSavedReports()]);
        return [...searches, ...reports];
    }

    public static async deleteSavedQuery(queryId: number): Promise<void> {
        return await DataAccess.delete<void>(`/ingestion/search-report/saved-query/${queryId}.json`);
    }

    public static isJsonField(fieldDef: IngestionFieldDefinition): boolean {
        return fieldDef.fieldType ? ["OBJECT_OCCURRENCE", "REGISTRANTS"].indexOf(fieldDef.fieldType) !== -1 : false;
    }
}
